import { Login as LoginIcon } from '@mui/icons-material';
import {
    Alert,
    AppBar,
    Box,
    Button,
    CircularProgress,
    Container,
    TextField,
    Toolbar,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

const LoginForm = ({ onSubmit, attempts }) => {
    const [formData, setFormData] = useState({ campId: '', passcode: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [unauthorized, setUnauthorized] = useState(false);
    const [verifying, setVerifying] = useState(false); // pre-screen verifier state

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const authenticateWithAPI = async (campId, passcode) => {
        try {
            const response = await fetch('https://camps.asgeyehospital.com/asg-camps-backend/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ camp_id: campId, passcode })
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) {
                return { success: true, campData: data.camp_data, status: response.status };
            }
            return { success: false, message: data.message || 'Authentication failed', status: response.status };
        } catch (error) {
            return { success: false, message: 'Network error. Please try again.', status: 0 };
        }
    };

    // Pre-verify: if URL has camp_id & passcode, verify before showing form (do not populate inputs)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlCampId = params.get('camp_id');
        const urlPasscode = params.get('passcode');
        if (urlCampId && urlPasscode) {
            setVerifying(true);
            setUnauthorized(false);
            setError('');
            (async () => {
                const result = await authenticateWithAPI(urlCampId, urlPasscode);
                setVerifying(false);
                if (result.success) {
                    // Store the access token for subsequent API calls
                    localStorage.setItem('accessToken', result.campData.access_token);
                    localStorage.setItem('campData', JSON.stringify(result.campData));
                    await onSubmit({ campId: urlCampId, passcode: urlPasscode, campData: result.campData });
                } else {
                    if (result.status === 401 || result.status === 403) {
                        setUnauthorized(true);
                        setError(result.message || 'Unauthorized access');
                    } else {
                        // For other errors (like "Camp has not started yet"), show error without marking as unauthorized
                        setError(result.message || 'Authentication failed');
                    }
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const result = await authenticateWithAPI(formData.campId, formData.passcode);
            if (result.success) {
                // Store the access token for subsequent API calls
                localStorage.setItem('accessToken', result.campData.access_token);
                localStorage.setItem('campData', JSON.stringify(result.campData));
                await onSubmit({ campId: formData.campId, passcode: formData.passcode, campData: result.campData });
            } else {
                if (result.status === 401 || result.status === 403) setUnauthorized(true);
                setError(result.message);
            }
        } catch (error) {
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <Toolbar sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'center' }, justifyContent: { xs: 'center', md: 'space-between' }, py: { xs: 1.5, md: 0 } }}>
                    <img src={process.env.PUBLIC_URL + '/asg-logo.png'} alt="ASG Eye Care" style={{ maxHeight: '40px', width: 'auto', objectFit: 'contain' }} />
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="h6" sx={{ color: '#1e3a8a', fontWeight: 700, textAlign: { xs: 'center', md: 'left' }, mt: { xs: 1, md: 0 } }}>
                        ASG Eye Care Camp - {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm">
                <Box sx={{ minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                    <Box sx={{ width: '100%', maxWidth: 400 }}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#1e3a8a', fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
                            Login To Camp
                        </Typography>

                        {/* Pre-verifier loader when URL params are present */}
                        {verifying ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={28} sx={{ mr: 2 }} />
                                <Typography variant="body1" color="text.secondary">Verifying access…</Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Show unauthorized alert for 401/403 errors */}
                                {unauthorized ? (
                                    <Alert severity="error" variant="filled" sx={{ mb: 3 }}>
                                        Unauthorized Access. The link is invalid or you don't have permission.
                                    </Alert>
                                ) : error ? (
                                    /* Show other errors (e.g., "Camp has not started yet") and hide the form */
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        {error}
                                    </Alert>
                                ) : (
                                    <>
                                        {/* Show attempt errors only when manually logging in */}
                                        {attempts > 0 && (
                                            <Alert severity="error" sx={{ mb: 3 }}>
                                                {`Invalid credentials. ${3 - attempts} attempts remaining.`}
                                            </Alert>
                                        )}

                                        {/* Show login form only if no errors */}
                                        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <TextField
                                                fullWidth
                                                label="Camp ID"
                                                name="campId"
                                                value={formData.campId}
                                                onChange={handleInputChange}
                                                required
                                                variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a8a' } } }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Passcode"
                                                name="passcode"
                                                type="password"
                                                value={formData.passcode}
                                                onChange={handleInputChange}
                                                required
                                                variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a8a' } } }}
                                            />
                                            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />} sx={{ backgroundColor: '#1e3a8a', '&:hover': { backgroundColor: '#1e40af' }, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: '5px', textTransform: 'none', boxShadow: '0 4px 14px rgba(30, 58, 138, 0.3)' }}>
                                                {loading ? 'Authenticating...' : 'Login'}
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default LoginForm;
